<script context="module" lang="ts">
	import shopify from '$lib/api/ecom/shopify';
	import type {
		CollectionInterfaceItem,
		ProductInterfaceItem
	} from '$lib/types/ecom/interfaceTypes';
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import { gql } from 'graphql-request';

	function uniq(a: any[]) {
		const ids: string[] = [];
		return a.filter((item) => {
			if (!ids.includes(item.id)) {
				ids.push(item.id);
				return true;
			} else {
				return false;
			}
		});
	}

	export async function load(data: Props, params: any, additionalLoadData: any = {}) {
		const custom_products = data.custom_products
			? JSON.parse(data.custom_products)
			: (undefined as ProductInterfaceItem[] | undefined);
		const recommendation_product_id = additionalLoadData.product?.id;
		/*
		const recommendation_product = JSON.parse(data.recommendation_product) as
			| ProductInterfaceItem[]
			| undefined;
			*/
		const collection = data.collection
			? JSON.parse(data.collection)
			: (undefined as CollectionInterfaceItem[] | undefined);

		let products: any[] | undefined;
		let collections: any[] = [];

		if (data.type === 'custom') {
			products = (
				await shopify.getProductsById(
					custom_products
						?.map((item: any) => item.selectedProduct?.node?.id)
						.filter((item: any): item is string => !!item) ?? [],
					additionalLoadData.data.location,
					params.lang
				)
			)?.nodes;
		} else if (data.type === 'recommended') {
			products = (
				await shopify.getProductRecommendations(
					recommendation_product_id ?? '', // TODO: check if we can use the variant here
					additionalLoadData.data.location
				)
			)?.productRecommendations;
		} else if (data.type === 'collection') {
			const collectionHandles =
				collection?.map((col: any) => col.selectedCollection?.node?.handle) ?? [];
			const collectionIds = collection?.map((col: any) => col.selectedCollection?.node?.id) ?? [];

			const allProducts = await Promise.all(
				collectionHandles.map(async (handle: any) => {
					const products = await shopify.getCollectionProducts(
						handle,
						undefined,
						20,
						undefined,
						undefined,
						additionalLoadData.data.location,
						params.lang
					);

					return products?.collection?.products?.edges?.map((item: any) => ({
						...item.node,
						collection: handle
					}));
				})
			);

			collections = ((await shopify.getCollections(params.lang)) as any)?.collections?.edges.filter(
				(item: any) => {
					return collectionIds.includes(item.node.id);
				}
			);

			// products = allProducts.flatMap((data) => data?.map((item: any) => item.node));
			products = allProducts.flat();
			// products = allProducts.flatMap((data) => data);
		}

		products = products?.filter(
			(item) => item?.product_type?.value === additionalLoadData.data.customerType
		);

		products = uniq(products);

		return {
			products,
			collections: collections ?? []
		};
	}

	export const config: SectionConfig = {
		collection: 'section_product_slider',
		availableFor: ['pages', 'product', 'special-pages', 'posts'],
		query: gql`
			id
			variant
			translations {
        languages_code {
          code
        }
        title
      }
			link {
				...Link
			}
			type
			custom_products
			recommendation_product
			collection

			enable_filter
    `,
		load
	};

	// optional: define a type for the props (note: injectTranslations is applied)
	type Props = {
		id: string;
		title: string;
		products: string;
		link: any;
		type: 'custom' | 'recommended' | 'collection';
		variant: 'slider-small' | 'slider-large';

		custom_products: string;
		recommendation_product_id: string;
		collection: string;
		enable_filter: boolean;
	};
</script>

<script lang="ts">
	import ProductCard from '$lib/components/ecom/ProductCard.svelte';
	import Section from '$lib/components/layout/Section.svelte';
	import FullWidthSlider from '$lib/components/util/FullWidthSlider.svelte';
	import type { SwiperOptions } from 'swiper/types/swiper-options';
	import Media from '$lib/components/media/Media.svelte';
	import { isTypeSystemDefinitionNode } from 'graphql';
	import { fade, fly, slide } from 'svelte/transition';
	import { fadein, fadeout } from '$lib/util/transitions';
	import Swiper from 'swiper';
	import GlobalMedia from '$lib/components/media/GlobalMedia.svelte';
	import DirectusLink from '$lib/components/navigation/DirectusLink.svelte';
	import Text from '$lib/components/Text.svelte';

	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)
	export let additionalData: Awaited<ReturnType<typeof load>>;

	const swiperOptions: SwiperOptions = {
		loop: false,
		centeredSlides: false,
		initialSlide: 0
	};

	let selectedCategory: number | null = null;

	function changeCategory(idx: number | null) {
		selectedCategory = selectedCategory === idx ? null : idx;
		swiperRef?.slideTo(0);
	}

	$: filteredProducts =
		selectedCategory != null
			? additionalData.products?.filter((product) =>
					product?.collections?.edges?.find(
						(item) =>
							item?.node?.id === additionalData.collections?.[selectedCategory as number]?.node?.id
					)
			  )
			: additionalData.products ?? [];

	let swiperRef: Swiper | undefined = undefined;

	$: showCategoriesFilter = data.type === 'collection' && data.enable_filter;

	$: initialized = !!swiperRef;
</script>

<!-- TODO: add support for preselected variants with custom type -->

{#if additionalData?.products && additionalData?.products?.length > 0}
	<Section type="full" class="section-product-slider {initialized ? 'initialized' : ''}">
		{#if showCategoriesFilter}
			<Section class="product-slider-filter">
				<div class="categories-wrapper">
					<div class="categories-box">
						<div class="title">{data.title}</div>
						<div class="category more mobile">
							<DirectusLink {...data.link}>
								<div class="icon-wrapper">
									<GlobalMedia key="icon-more" />
								</div>
								<div class="category-name">more</div>
							</DirectusLink>
						</div>
						<div class="categories">
							{#each additionalData.collections as { node: item }, i}
								<div
									class="category"
									class:active={selectedCategory === i}
									on:click={() => changeCategory(i)}
								>
									<div class="icon-wrapper">
										<img
											class="icon"
											src={item.image_inactive?.reference?.image?.url}
											alt=""
											loading="lazy"
										/>
										<img
											class="hover-icon"
											src={item.image_active?.reference?.image?.url}
											alt=""
											loading="lazy"
										/>
									</div>
									<div class="category-name">
										{item.title}
									</div>
								</div>
							{/each}

							<DirectusLink {...data.link}>
								<div class="category more desktop">
									<div class="icon-wrapper">
										<Media {...data.link?.icon} />
									</div>
									<div class="category-name">{data.link?.display_text}</div>
								</div>
							</DirectusLink>

							<!-- <a class="category more desktop">
								<div class="icon-wrapper">
									<GlobalMedia key="icon-more" />
								</div>
								<div class="category-name">more</div>
							</a> -->
						</div>
					</div>
					<div class="navigation">
						<button
							class="slider-nav prev"
							on:click={() => {
								swiperRef?.slidePrev(400);
							}}
						>
							<GlobalMedia key="icon-arrow-left" />
						</button>
						<button
							class="slider-nav next"
							on:click={() => {
								swiperRef?.slideNext(400);
							}}
						>
							<GlobalMedia key="icon-arrow-right" />
						</button>
					</div>
				</div></Section
			>
		{:else}
			<Section class="content-section">
				<div class="content-wrapper">
					{#if data.title}
						<div class="title">{data.title}</div>
					{/if}

					{#if data.link}
						<DirectusLink {...data.link}>
							<div class="link">
								<div class="text">{data.link?.display_text}</div>
								<div class="icon-wrapper">
									<GlobalMedia key="icon-arrow-right" />
								</div>
							</div>
						</DirectusLink>
					{/if}
				</div>
			</Section>
		{/if}
		{#if filteredProducts && filteredProducts.length > 0}
			<FullWidthSlider
				name="product-slider-{data.id}"
				type="multi"
				pagination={false}
				navigation={!showCategoriesFilter}
				options={swiperOptions}
				arrowType="arrow"
				slidesPerView={2}
				slidesPerViewPhone={2}
				slidesPerViewTablet={3}
				slidesPerViewDesktopSmall={4}
				slidesPerViewDesktop={5}
				bind:swiper={swiperRef}
			>
				{#each filteredProducts ?? [] as product, idx}
					{#key product.id + ' ' + selectedCategory + ' ' + idx}
						<div class="swiper-slide" in:fadein out:fadeout>
							<ProductCard {product} />
						</div>
					{/key}
				{/each}
			</FullWidthSlider>
		{:else}
			<div class="no-products">
				<Text key="ecom_no_products" />
			</div>
		{/if}
	</Section>
{/if}

<style lang="scss">
	:global(.product-slider-filter) {
		margin-block: 0 !important;

		@include media-between(phone, tablet) {
			padding: 0 !important;
		}
	}

	:global(.section-product-slider:not(.initialized)) {
		opacity: 0;
		max-height: 50vh;
	}
	:global(.section-product-slider.initialized) {
		opacity: 1;
		@include transition(all);
	}

	:global(.content-section) {
		margin-bottom: 0 !important;
	}

	.no-products {
		text-align: center;
		width: 100%;
		@include font(h6);
	}

	.content-wrapper {
		display: flex;
		align-items: flex-end;
		justify-content: flex-start;
		gap: v(content-gap-sm);
		@media (max-width: 600px) {
			margin-bottom: v(content-gap);
		}

		.title {
			@include font(h3);
		}

		.link {
			@include font(h6);
			display: flex;
			gap: v(content-gap-xs);
			align-items: center;
			padding-bottom: 0.6em;
			color: color(black);
			@include transition(all);
			white-space: nowrap;
			nowrap &:hover {
				opacity: 0.5;
			}

			.icon-wrapper {
				height: 0.75rem;
				width: 20px;
			}
		}
		:global(.navigation) {
			border: solid red;
		}
	}

	.categories-wrapper {
		display: flex;
		justify-content: center;
		gap: v(content-gap-sm);
		margin-bottom: v(content-padding);
	}

	.navigation,
	.categories-box {
		background: color(white);
		border-radius: v(border-radius);
	}

	.navigation {
		flex-shrink: 0;
		flex-grow: 0;

		padding-bottom: v(content-gap);
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 0 v(content-padding-sm);
		gap: v(content-gap-sm);

		.slider-nav {
			border: none;
			background: transparent;
			transition: all 0.3s ease;
			width: 16px;
			height: 16px;

			padding: 0;

			&:hover {
				filter: brightness(0.2);
			}
		}

		@include media(tablet) {
			display: none;
		}
	}

	.categories-box {
		width: auto;
		display: flex;
		flex: 1;
		justify-content: flex-start;
		gap: v(content-gap-2xl);
		padding: v(content-padding-sm) v(content-padding);

		overflow-x: auto;

		@include media(tablet) {
			width: 100%;
			gap: v(content-gap-sm);
			display: grid;
			grid-template-columns: 1fr 1fr;
			padding-bottom: 0;

			overflow-x: unset;
		}

		.title {
			@include font(h4);
			// width: 12ch;
			align-self: center;

			@include media(tablet) {
				@include font(h5);
			}
		}
	}

	.categories {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: v(content-gap-xl);
		flex: 1;

		grid-column: 1 / span 2;

		@include media(tablet) {
			justify-content: flex-start;
			gap: v(content-gap-sm);
			overflow-x: auto;
			margin-inline: calc(-1 * v(content-padding));
			padding-inline-start: v(content-padding);
			padding-bottom: v(content-gap-sm);
		}
	}

	.category {
		display: flex;
		position: relative;
		align-items: center;
		gap: v(content-gap-sm);
		@include font(h5-collection);
		cursor: pointer;
		flex-shrink: 0;

		.category-name {
			@include min-media(tablet) {
				max-width: 8ch;
			}
		}

		@include media(tablet) {
			&:not(.more) {
				border: solid 1px color(black);
				border-radius: v(border-radius);
				padding: v(content-padding-xs);
				transition: all 0.3s ease;
				@include font(h6);

				&.active {
					background: color(primary);
					border-color: color(primary);
				}
			}
		}

		&.more {
			color: color(black);
			&.mobile {
				display: flex;
				justify-content: flex-end;
				:global(a) {
					color: color(black);
					display: flex;
					gap: v(content-gap-sm);
					align-items: center;
				}
				@include min-media(tablet) {
					display: none;
				}
			}
			&.desktop {
				@include media(tablet) {
					display: none;
				}
			}

			.icon-wrapper {
				width: 2rem;
			}
		}

		@include min-media(tablet) {
			&:hover,
			&.active {
				.category-name,
				.all {
					text-decoration: underline;
				}
				.icon {
					opacity: 0;
				}
				.hover-icon {
					opacity: 1;
				}
			}
		}

		@include media(tablet) {
			&:not(.more) {
				.icon-wrapper {
					display: none;
				}
			}
		}

		.icon-wrapper {
			position: relative;
			width: 2rem;
			height: 2rem;
		}
		.icon,
		.hover-icon {
			width: 2rem;
			height: 2rem;
			transition: opacity 0.3s ease;
			position: absolute;
			top: 0;
			inset-inline-start: 0;
		}
		.icon {
			opacity: 1;
		}
		.hover-icon {
			opacity: 0;
		}
	}

	.variant-slider-small {
		.swiper-slide {
			width: max(450px, 25vw);

			@include media(phone-sm) {
				width: calc(80vw);
			}
		}
	}

	.variant-slider-large {
		.swiper-slide {
			width: max(450px, 30vw);

			@include media(phone-sm) {
				width: calc(80vw);
			}
		}
	}
</style>
